import { useEffect, useState } from 'react'
import { Transition } from '@headlessui/react'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { Loading } from '../../shared/components/Loading'

export default function Carousel({
    items = [],
    autoPlay = false,
    interval = 3000,
    size = 'medium',
    loading = true,
    aspectRatio = null, // example: "19/6" or "4/3"
}) {
    const [current, setCurrent] = useState(0)
    const total = items.length
    const { t } = useTranslation()

    const next = () => setCurrent(prev => (prev + 1) % total)
    const prev = () => setCurrent(prev => (prev - 1 + total) % total)

    const heightClass =
        {
            screen: 'h-[60vh] sm:h-screen',
            large: 'h-[60vh] sm:h-[80vh] md:h-[70vh]',
            medium: 'h-48 sm:h-64 md:h-80',
        }[size] || 'h-48 sm:h-64'

    useEffect(() => {
        if (!autoPlay || total <= 1) return

        const timer = setInterval(() => {
            setCurrent(prev => (prev + 1) % total)
        }, interval)

        return () => clearInterval(timer)
    }, [autoPlay, total, interval])

    if (!items || items.length === 0) {
        if (loading) return <Loading />

        return (
            <div className={`${heightClass} bg-gray-100 flex items-center justify-center`}>
                {t('status.no_items')}
            </div>
        )
    }

    return (
        <div className='relative w-full overflow-hidden'>
            <div
                className={`relative w-full overflow-hidden ${aspectRatio ? '' : heightClass}`}
                style={{
                    aspectRatio: aspectRatio || undefined,
                }}
            >
                {items.map((item, index) => {
                    const isActive = index === current

                    return (
                        <Transition
                            key={index}
                            show={isActive}
                            enter='transition-all duration-700 ease-out'
                            enterFrom='opacity-0 translate-x-10'
                            enterTo='opacity-100 translate-x-0'
                            leave='transition-all duration-700 ease-in'
                            leaveFrom='opacity-100 translate-x-0'
                            leaveTo='opacity-0 -translate-x-10'
                            className={`absolute inset-0 ${isActive ? 'z-20' : 'z-10'}`}
                            style={{
                                pointerEvents: isActive ? 'auto' : 'none',
                            }}
                        >
                            <div className='flex items-center justify-center w-full h-full'>
                                {/* Force child to fill */}
                                <div className='flex items-center justify-center w-full h-full'>
                                    {item}
                                </div>
                            </div>
                        </Transition>
                    )
                })}
            </div>

            {total > 1 && (
                <>
                    <button
                        onClick={prev}
                        className='absolute z-40 p-2 transform -translate-y-1/2 bg-white rounded-full shadow-lg sm:p-3 left-2 top-1/2 bg-opacity-60 hover:bg-opacity-80'
                        aria-label='Previous slide'
                    >
                        <FaChevronLeft className='text-lg text-black sm:text-xl' />
                    </button>

                    <button
                        onClick={next}
                        className='absolute z-40 p-2 transform -translate-y-1/2 bg-white rounded-full shadow-lg sm:p-3 right-2 top-1/2 bg-opacity-60 hover:bg-opacity-80'
                        aria-label='Next slide'
                    >
                        <FaChevronRight className='text-lg text-black sm:text-xl' />
                    </button>

                    <div className='absolute z-40 flex gap-2 transform -translate-x-1/2 sm:gap-4 bottom-4 left-1/2'>
                        {items.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrent(index)}
                                className={`h-3 w-3 sm:h-4 sm:w-4 rounded-full transition-colors duration-300 ${
                                    index === current
                                        ? 'bg-yellow-500 scale-110'
                                        : 'bg-gray-400 hover:bg-gray-300'
                                }`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}
